import type {
  DubManifestDTO,
  ResolvedDubManifest,
} from '@src/repositories/DubbingRepository';

/**
 * The resolved dub description, held in memory inside the editor.
 *
 * **Why this exists at all.** The dub engine runs when the timeline is turned
 * into a document, and the preview serialiser is a plain synchronous method
 * called on every editor change and all the way through playback and scrubbing.
 * The description of the generated audio arrives over the network, and the
 * addresses inside it are signed and stop being served after an hour. A
 * synchronous method cannot fetch, and a signed address must not be written into
 * anything that is saved. So the description is fetched by the surfaces above
 * and left here, and the engine reads it and never fetches.
 *
 * If nothing is here for the language being asked about, that pass of the engine
 * emits no dub audio. It is not an error: the description's arrival changes what
 * this holds, the editor is told, and the next pass places the dub.
 *
 * Kept per language rather than one at a time, which is what makes switching
 * language and switching back produce the same audio both times with nothing
 * regenerated.
 */

/** One language's dubbed audio, as the server described it. */
export interface ResolvedDubDescription {
  /** The dub's target language tag, for example `ru-RU`. */
  language: string;
  /** Which lines were dubbed, where each sits, and how long each runs. */
  manifest: DubManifestDTO;
  /** The signed address of each recorded file, by its identifier. */
  renditionUrls: Record<string, string>;
  /**
   * When these addresses were minted, as milliseconds since the epoch. Read
   * only to answer whether the description is old enough to be worth fetching
   * again.
   */
  resolvedAt: number;
}

/**
 * What the dub engine needs from the world outside the timeline, and nothing
 * else. A narrow interface on purpose: the timeline holds one of these, and
 * everything it can ask is on this list.
 */
export interface DubSource {
  /** This language's description, or nothing when it has not arrived. */
  describe(language: string): ResolvedDubDescription | undefined;
  /**
   * The kept music bed's signed address, or `null` when the story has never
   * been separated or its separation found no music worth keeping.
   */
  musicBedUrl(): string | null;
  /**
   * The story's separated voice's signed address, which the lead-in moves
   * ahead of the dub, or `null` when the story has never been separated.
   */
  voiceStemUrl(): string | null;
  /**
   * "Is this the clip's original video?" — the picture whose level the dub
   * drops so the dubbed voice is what is heard. A function because only the
   * surfaces above know which recording a story is built from.
   */
  isOriginalVideoElement(probe: unknown): boolean;
}

/**
 * How long a signed address is worth trusting. The backend signs for longer
 * than this; the shorter figure is deliberate, so a refresh is asked for while
 * the addresses in hand still work.
 */
export const DUB_ADDRESS_LIFETIME_MS = 60 * 60 * 1000;

export class DubDescriptionStore implements DubSource {
  private byLanguage = new Map<string, ResolvedDubDescription>();

  /**
   * PLAT-11905 — told whenever what this holds changes (`remember`, `forget`,
   * `clear`). The editor listens, so the captions switch to the dub's timing in
   * the same pass as the voice when a description arrives, and back when it
   * goes.
   */
  private changeListeners = new Set<() => void>();

  /**
   * PLAT-11905 — told when something asks for a language's description to be
   * fetched again (a caption produce in a dubbed language). The fetching hook
   * above listens; this holder never fetches.
   */
  private refreshListeners = new Set<(language: string) => void>();

  private bedUrl: string | null = null;

  private voiceUrl: string | null = null;

  private originalVideoProbe: (probe: unknown) => boolean = () => false;

  describe(language: string): ResolvedDubDescription | undefined {
    if (typeof language !== 'string' || language === '') return undefined;
    return this.byLanguage.get(language);
  }

  musicBedUrl(): string | null {
    return this.bedUrl;
  }

  voiceStemUrl(): string | null {
    return this.voiceUrl;
  }

  isOriginalVideoElement(probe: unknown): boolean {
    return this.originalVideoProbe(probe);
  }

  /**
   * Put one language's description in, replacing whatever was there.
   *
   * `resolved` is exactly what the manifest fetch answers, so a caller hands
   * over what it received rather than taking it apart first.
   */
  remember(
    language: string,
    resolved: ResolvedDubManifest,
    resolvedAt: number = Date.now(),
  ): void {
    if (typeof language !== 'string' || language === '') return;
    if (!resolved || typeof resolved !== 'object') return;
    this.byLanguage.set(language, {
      language,
      manifest: resolved.manifest,
      renditionUrls: resolved.renditionUrls,
      resolvedAt,
    });
    this.notifyChanged();
  }

  /**
   * Drop one language, for the case the fetch answers that the story no longer
   * has a dub in it. Dropping is what takes its audio back out of the next
   * document rather than leaving a dead one in.
   */
  forget(language: string): void {
    this.byLanguage.delete(language);
    this.notifyChanged();
  }

  /** Drop everything. Called when a different story is loaded. */
  clear(): void {
    this.byLanguage.clear();
    this.bedUrl = null;
    this.voiceUrl = null;
    this.notifyChanged();
  }

  /** Be told whenever what this holds changes. Returns the unsubscribe. */
  onChange(listener: () => void): () => void {
    this.changeListeners.add(listener);
    return () => {
      this.changeListeners.delete(listener);
    };
  }

  /**
   * Ask for this language's description to be fetched again. Whoever fetches
   * for that language answers; with nobody listening it does nothing.
   */
  requestRefresh(language: string): void {
    if (typeof language !== 'string' || language === '') return;
    for (const listener of [...this.refreshListeners]) listener(language);
  }

  /** Be told when a refresh is asked for. Returns the unsubscribe. */
  onRefreshRequested(listener: (language: string) => void): () => void {
    this.refreshListeners.add(listener);
    return () => {
      this.refreshListeners.delete(listener);
    };
  }

  private notifyChanged(): void {
    for (const listener of [...this.changeListeners]) listener();
  }

  /**
   * Is this language's description missing, or old enough that its addresses
   * are worth fetching again? The surfaces above ask this on a timer; the
   * engine never does.
   */
  needsRefresh(language: string, now: number = Date.now()): boolean {
    const held = this.describe(language);
    if (!held) return true;
    return now - held.resolvedAt >= DUB_ADDRESS_LIFETIME_MS;
  }

  /** Every language held, in the order they were first put in. */
  languages(): string[] {
    return [...this.byLanguage.keys()];
  }

  setMusicBedUrl(url: string | null): void {
    this.bedUrl = typeof url === 'string' && url !== '' ? url : null;
  }

  setVoiceStemUrl(url: string | null): void {
    this.voiceUrl = typeof url === 'string' && url !== '' ? url : null;
  }

  setOriginalVideoProbe(probe: (probe: unknown) => boolean): void {
    this.originalVideoProbe = typeof probe === 'function' ? probe : () => false;
  }
}
